// ------------------------------------------------------------------------------------------------------------
// [ifHasPermission] Structural Directive
// - Render if 'true' or ANY of the given permissions are enabled for the current user
// ------------------------------------------------------------------------------------------------------------
import { Directive, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { PermissionDTO, PermissionKey } from '@class/commons/permissions/permission-constants';
import { PermissionsService } from '@service/permissions/permissions.service';
import { isBoolean } from 'lodash';

export enum PermissionMatch {
  Any = 1,
  All = 2,
}

/**
 * TODO
 * not sure this belongs here
 * but couldn't find a better place to put this comment
 * before using this directive, we were directly binding the function in ngIf
 * that were getting called all the time so that it can pick the value if the states changed in the permissions
 * but with this directive, it solves that problem and it's efficient but this introduce a new problem
 *
 * technically we shouldn't load
 *  1. home/landing page unless we have the user level permissions
 *  2. unit/portfolio/vpp page unless we have unit/portfolio/vpp data as permission lies there
 * but the current structure is not properly following that
 * so where ever we are using this directive
 * we need to make sure that we have the data first otherwise the directive will run and return false
 * as the permission store gets updated but it doesn't execute the directive as the input values don't change
 * we can do 2 things
 *  1. The right thing, that wait for the data to arrive before rendering any thing - this is preferred/efficient way
 *  2. dodgy and easy way that publish the permission store - and every time it gets updated we publish it
 *     and grab it in here and then run the execute again
 */

@Directive({
  selector: '[ifHasPermission]',
})
export class IfHasPermissionDirective implements OnInit {
  private _permissions: Array<PermissionKey> | boolean;
  @Input() set ifHasPermission(value: Array<PermissionKey> | boolean) {
    this._permissions = value;
    // debug log
    // console.log('IfHasPermissionDirective.set', { permissions: this._permissions });
  }
  private _match: PermissionMatch = PermissionMatch.Any;
  @Input() set ifHasPermissionMatch(value: PermissionMatch) {
    this._match = value;
    // debug log
    // console.log('IfHasPermissionDirective.Match', { value });
  }
  private _disableOnFail: boolean = false;
  @Input() set ifHasPermissionDisableOnFail(value: boolean) {
    this._disableOnFail = value;
    // debug log
    // console.log('IfHasPermissionDirective.DisableOnFail', { value });
  }
  private _externalPermissions: Array<PermissionDTO> = null;
  @Input() set ifHasPermissionExternalPermissions(value: Array<PermissionDTO>) {
    // NOTE: Temporary Input to support VPP dynamic permission
    //       VPP is getting refactored so we may be able to remove this after refactor.
    this._externalPermissions = value;
    // debug log
    // console.log('IfHasPermissionDirective.ExternalPermissions', { value });
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    /**
     * executing the directive on ngOnInit, if we execute it on AfterViewInit
     * the ng hooks will in the main component where we are using this directive
     * will throw errors that view has changed
     * and that is true
     * e.g., parent-component & child-component and
     * <parent-component> <child-component *ifHasPermission=[somePermission]></child-component> </parent-component>
     * if we have permission, it'll render the component otherwise it'll hide it
     * onAfterViewInit - it'll create the component when the parent component view has been init
     * but the parent component will run ngAfterViewChecked and will see a new component there that wasn't there before
     * and will throw error
     * so has to execute it onInit
     */
    this.execute(this._permissions, this._externalPermissions, this._match, this._disableOnFail);
  }

  private execute(
    permissions: Array<PermissionKey> | boolean,
    extPermissions: Array<PermissionDTO>,
    match: PermissionMatch,
    onFail: boolean,
  ): void {
    // looks like this function is committing crime of hidden state but not sure we can get over this
    // we need to update the view container...

    // if we were given a boolean (true/false) use that
    // otherwise use the permissions service to check current user permissions

    // debug log
    // console.log('IfHasPermissionDirective.execute', { this: this });

    const hasPermission = this.checkPermissions(permissions, extPermissions, match);
    // console.log('I am going to execute for: ', permissions, ' and do we have permission? ', hasPermission);

    if (hasPermission) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      // default (not disable) - do not render the element
      if (!onFail) {
        this.viewContainer.clear();
      }
      // disable - still render but with the disabled attribute set
      else {
        // render the view
        const viewContainer = this.viewContainer.createEmbeddedView(this.templateRef);
        // disable the root element
        const rootElem = viewContainer.rootNodes[0];
        if (rootElem) {
          // console.log('IfHasPermissionDirective.execute.disable', { rootElem });
          rootElem.disabled = true;
        }
      }
    }
  }

  private checkPermissions(
    permissions: Array<PermissionKey> | boolean,
    extPermissions: Array<PermissionDTO>,
    match: PermissionMatch,
  ): boolean {
    if (isBoolean(permissions)) {
      return permissions;
    } else {
      return this.checkPermissionsFromList(permissions, extPermissions, match);
    }
  }

  private checkPermissionsFromList(
    permissions: Array<PermissionKey>,
    extPermissions: Array<PermissionDTO>,
    match: PermissionMatch,
  ): boolean {
    if (extPermissions?.length) {
      return this.haveAnyExternalPerm(permissions, extPermissions);
    } else {
      return this.checkFromMatchCases(permissions, match);
    }
  }

  private checkFromMatchCases(permissions: Array<PermissionKey>, match: PermissionMatch): boolean {
    let res = false;
    switch (match) {
      case PermissionMatch.Any:
        res = this.haveAnyPerm(permissions);
        break;
      case PermissionMatch.All:
        res = this.haveAllPerm(permissions);
        break;
    }
    return res;
  }

  private haveAnyExternalPerm(permissions: Array<PermissionKey>, extPermissions: Array<PermissionDTO>): boolean {
    return this.permissionsService.anyExternal(permissions, extPermissions);
  }
  private haveAnyPerm(permissions: Array<PermissionKey>): boolean {
    return this.permissionsService.any(permissions);
  }
  private haveAllPerm(permissions: Array<PermissionKey>): boolean {
    return this.permissionsService.all(permissions);
  }
}
